import { useEffect, useRef, useState } from "react";
import { BookingDatePicker } from "./BookingDatePicker";
import { parseIsoDate, fmtIsoDate } from "../DateSelector/dateUtils";
import css from "./ModalHeader.module.css";

/**
 * Modal head (redesign B.3.h — compact).
 *
 *   row1: Дьявольский Мотель                                ×
 *   row2: [🕐 10:30–13:45] [▮▮▯ 195 мин]
 *
 * Всё в одну мета-строку компактных чипов (~2× ниже прошлой версии).
 * Длительность — мини-сегментированная полоска (квест/доп/чай) + итог
 * «195 мин» (cyan, лёгкий glow). Разбивка минут — в title чипа. Дата-чип
 * кликабелен → календарь поверх.
 */
export interface ModalHeaderProps {
  questName: string;
  /** Booking id for edit-state (renders "#ID" next to quest name). */
  bookingId?: number;
  slotTime: string;
  endTime: string;
  baseDuration: number;
  teaMin: number;
  /** Доп.услуги (МК) суммарная длительность, мин. */
  extrasDuration?: number;
  totalDuration: number;
  tea: boolean;
  statusLabel: string;
  /** ISO "YYYY-MM-DD" — the date the booking belongs to (override-able). */
  curDateStr: string;
  /** Fired when the user picks a new date in the calendar popover. */
  onDateChange?: (newIso: string) => void;
  onClose: () => void;
}

/* Легенда шкалы (long-press 480мс — стандарт жестов A43). */
const LEGEND_PRESS_MS = 480;

export function ModalHeader({
  questName,
  bookingId,
  slotTime,
  endTime,
  baseDuration,
  teaMin,
  extrasDuration = 0,
  totalDuration,
  tea,
  statusLabel,
  curDateStr,
  onDateChange,
  onClose,
}: ModalHeaderProps) {
  const [calOpen, setCalOpen] = useState(false);
  /* Легенда цветов шкалы: long-press по чипу длительности (тап мимо/Esc — закрыть). */
  const [legendOpen, setLegendOpen] = useState(false);
  const pressT = useRef<number | undefined>(undefined);
  const legendPressStart = () => {
    window.clearTimeout(pressT.current);
    pressT.current = window.setTimeout(() => setLegendOpen(true), LEGEND_PRESS_MS);
  };
  const legendPressEnd = () => window.clearTimeout(pressT.current);
  useEffect(() => {
    if (!legendOpen) return;
    const out = () => setLegendOpen(false);
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setLegendOpen(false); } };
    const t = window.setTimeout(() => document.addEventListener("pointerdown", out, true), 0);
    document.addEventListener("keydown", key, true);
    return () => { window.clearTimeout(t); document.removeEventListener("pointerdown", out, true); document.removeEventListener("keydown", key, true); };
  }, [legendOpen]);

  /* Date chip text: "14.05 чт" derived from "2026-05-14". */
  const dateChip = (() => {
    const parts = curDateStr.split("-");
    const mo = parts[1] ?? "";
    const d = parts[2] ?? "";
    const dowNames = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
    const isoDow = (() => {
      const dt = curDateStr.split("-").map(Number);
      if (dt.length !== 3 || !dt[0] || !dt[1] || !dt[2]) return "";
      return dowNames[new Date(dt[0], dt[1] - 1, dt[2]).getDay()] ?? "";
    })();
    return `${d}.${mo} ${isoDow}`;
  })();

  const curDate = parseIsoDate(curDateStr) ?? new Date();

  /* Duration segments — proportional timeline (quest + extras + tea). */
  /* Порядок сегментов фиксирован: квест (зелёный) → чай (синий) → доп (цвет МК --mc, 11.10).
     Шкала видна ВСЕГДА (13.08: и с одним сегментом — без чайной была пустая);
     в легенде long-press показываются все типы, отсутствующие — приглушены. */
  const allParts = [
    { key: "quest", label: "Квест", minutes: baseDuration, cls: css.tQuest },
    { key: "tea", label: "Чайная", minutes: tea ? teaMin : 0, cls: css.tTea },
    { key: "extra", label: "Доп. услуги", minutes: extrasDuration, cls: css.tExtra },
  ];
  const parts = allParts.filter((p) => p.minutes > 0);

  return (
    <div className={css.head}>
      <div className={css.row1}>
        <h2 className={css.questName}>
          {questName}
          {bookingId ? <span className={css.bookingId}>#{bookingId}</span> : null}
        </h2>
        <button
          type="button"
          className={`${css.chip} ${css.chipBtn} ${css.dateChip}`}
          onClick={() => onDateChange && setCalOpen((v) => !v)}
          aria-expanded={calOpen}
          aria-haspopup="dialog"
          disabled={!onDateChange}
        >
          <span className={`${css.chipIco} ${css.icoCyan}`} aria-hidden="true"><CalendarIcon /></span>
          <span className={css.chipVal}>{dateChip}</span>
        </button>
        <button type="button" className={css.close} onClick={onClose} aria-label="Закрыть">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M2 2 L12 12 M12 2 L2 12" />
          </svg>
        </button>
      </div>

      <div className={css.row2}>
        <span className={css.chip}>
          <span className={`${css.chipIco} ${css.icoViolet}`} aria-hidden="true"><ClockIcon /></span>
          <span className={css.chipVal}>{slotTime} – {endTime}</span>
        </span>
        <span
          className={`${css.chip} ${css.durChip} ${parts.length > 0 ? css.durChipBar : ""}`}
          title={parts.map((p) => `${p.label} ${p.minutes}`).join(" · ")}
          aria-label={`Длительность ${totalDuration} минут; долгое нажатие — легенда цветов`}
          onPointerDown={legendPressStart}
          onPointerUp={legendPressEnd}
          onPointerLeave={legendPressEnd}
          onPointerCancel={legendPressEnd}
          onContextMenu={(e) => e.preventDefault()}
        >
          {parts.length > 0 ? (
            <span className={css.microBar} aria-hidden="true">
              {parts.map((p) => (
                <span key={p.key} className={`${css.microSeg} ${p.cls}`} style={{ flexGrow: p.minutes }} />
              ))}
            </span>
          ) : null}
          <span className={css.durTotal}>{totalDuration}<span className={css.durUnit}> мин</span></span>
          {legendOpen ? (
            <span className={css.legend} role="dialog" aria-label="Легенда шкалы">
              {allParts.map((p) => (
                <span key={p.key} className={`${css.legRow} ${p.minutes > 0 ? "" : css.legOff}`}>
                  <span className={`${css.legDot} ${p.cls}`} aria-hidden="true" />
                  <span className={css.legLbl}>{p.label}</span>
                  <span className={css.legMin}>{p.minutes} мин</span>
                </span>
              ))}
            </span>
          ) : null}
        </span>
      </div>

      <span className={css.srOnly}>{statusLabel}</span>

      {calOpen && onDateChange ? (
        <BookingDatePicker
          value={curDate}
          onChange={(next) => onDateChange(fmtIsoDate(next))}
          onClose={() => setCalOpen(false)}
        />
      ) : null}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="3" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
      <circle cx="12" cy="14.5" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3 1.8" />
    </svg>
  );
}
